/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import type { UserOrganizationMembership } from '@modules/cloud-api/v1/types'

type AuthMethod = 'username-and-password' | 'google' | 'microsoft' | 'byoidp'

// Auth methods where organization is inherently bound to the method
// (no need to pass organizationId separately)
const organizationBoundAuthMethods = ['byoidp'] as const
type OrganizationBoundAuthMethod = (typeof organizationBoundAuthMethods)[number]

export const isOrganizationBoundAuthMethod = (
  method: AuthMethod | null | undefined,
): method is OrganizationBoundAuthMethod =>
  organizationBoundAuthMethods.includes(method as OrganizationBoundAuthMethod)

export const isSocialAuthMethod = (
  method: AuthMethod | null | undefined,
): method is 'google' | 'microsoft' => method === 'google' || method === 'microsoft'

/**
 * Checks if the current authentication method aligns with the organization's enforced authentication method.
 *
 * @param authMethod - The user's current authentication method
 * @param orgEnforcedAuthMethod - The organization's enforced authentication method
 * @returns `true` if the auth method is compatible with the organization's enforcement:
 *   - For 'social' enforcement: returns true if authMethod is 'google' or 'microsoft'
 *   - For 'sso' enforcement: always returns false (SSO login always has orgId in session)
 *   - For no enforcement: returns true if authMethod is defined
 */
export const isOrgAuthAlignedWithAuthMethod = (
  authMethod: AuthMethod | null | undefined,
  orgEnforcedAuthMethod: UserOrganizationMembership['enforce_authentication_method'],
): boolean => {
  switch (orgEnforcedAuthMethod) {
    case 'social':
      return isSocialAuthMethod(authMethod)
    case 'sso':
      // login with SSO will always has oid in the session, we never need to do this comparison.
      return false
    default:
      return !!authMethod
  }
}
